import { ProductList } from "@/components/ProductList";
import { HistoryCreatedInfo } from "@/features/history/HistoryCreatedInfo";
import { OrderHistoryDescription } from "@/features/history/historyItems/OrderHistoryItem";
import { type History, type Order } from "@repo/api";
import { ListView, ListViewItem, Tab, TabPanel, Tabs } from "@repo/skapa";
import { t } from "@repo/translation";
import { hasItems, isDefined } from "@repo/utils";

import { CustomMessagesList } from "./CustomMessagesList";
import { CustomerDetails } from "./CustomerDetails";
import { OrderAttachments } from "./OrderAttachments";
import { AssociatedDrawings, OrderDetails } from "./OrderDetails";
import { ServiceList } from "./ServiceList";

interface ViewOrderModalProps {
  order?: Order;
  historyEntries?: History[];
  activeTab?: string;
}

export const OrderDetailsModal = ({
  order,
  historyEntries,
  activeTab,
}: ViewOrderModalProps) => {
  function getItemDetailsRecord(historyItems: History[] | undefined): any {
    const details: Record<string, any> = {};
    if (!historyItems) {
      return {};
    }

    for (const history of historyItems) {
      if (history.details?.items_updates) {
        for (const itemUpdate of history.details.items_updates) {
          if (itemUpdate.item_no) {
            details[itemUpdate.item_no] = {
              ...itemUpdate,
            };
          }
        }
      }
    }

    return details;
  }

  if (!order) {
    return <p>{t("prompt.no_details_found")}</p>;
  }

  return (
    <Tabs
      defaultActiveTab={activeTab}
      slideIndex={activeTab ? 5 : 0}
      tabs={[
        hasItems(historyEntries) ? (
          <Tab
            key="order-histories-tab"
            text={t("label.history")}
            tabPanelId="order-histories"
          />
        ) : null,
        <Tab
          key="order-details-tab"
          text={t("label.details")}
          tabPanelId="order-details"
        />,
        <Tab
          key="order-items-tab"
          text={t("label.articles")}
          tabPanelId="order-items"
        />,
        order?.billingContact ? (
          <Tab
            key="order-billing-address-tab"
            text={t("label.billing_address")}
            tabPanelId="order-billing-address"
          />
        ) : null,
        order?.deliveryContact ? (
          <Tab
            key="order-delivery-address-tab"
            text={t("label.delivery_address")}
            tabPanelId="order-delivery-address"
          />
        ) : null,
        hasItems(order?.providedServices) ||
        hasItems(order?.deliveryArrangements) ? (
          <Tab
            key="order-services-tab"
            text={t("label.services")}
            tabPanelId="order-services"
          />
        ) : null,
        hasItems(order?.planners) ? (
          <Tab
            key="order-associated-planner-drawing-tab"
            text={t("label.associated_drawing")}
            tabPanelId="order-associated-planner-drawing"
          />
        ) : null,

        hasItems(order?.fileAttachments) ? (
          <Tab
            key="order-attachment-tab"
            text={t("label.attachments")}
            tabPanelId="order-attachments"
          />
        ) : null,
        hasItems(order?.customMessages) ? (
          <Tab
            key="order-custom-messages-tab"
            text={t("label.messages")}
            tabPanelId="order-custom-messages"
          />
        ) : null,
      ].filter(isDefined)}
      tabPanels={[
        <TabPanel key="order-histories-tab" tabPanelId="order-histories">
          <ListView id="order-history-entries">
            {historyEntries?.map((history) => {
              return (
                <ListViewItem
                  key={history.id}
                  control="default"
                  title={history.title}
                  description={<OrderHistoryDescription history={history} />}
                  addOn={
                    <HistoryCreatedInfo
                      createdBy={history.createdBy}
                      createdAt={history.createdAt}
                    />
                  }
                />
              );
            })}
          </ListView>
        </TabPanel>,
        <TabPanel key="order-details-tab" tabPanelId="order-details">
          <OrderDetails order={order} />
        </TabPanel>,
        <TabPanel key="order-items-tab" tabPanelId="order-items">
          <ProductList
            product={order}
            amount={order?.amounts?.GoodsValue?.grossAmount}
            currency={order?.amounts?.GoodsValue?.currencyCode}
            itemDetailsRecord={getItemDetailsRecord(historyEntries)}
          />
        </TabPanel>,
        <TabPanel
          key="order-billing-address-tab"
          tabPanelId="order-billing-address"
        >
          <CustomerDetails contactData={order?.billingContact} />
        </TabPanel>,
        <TabPanel
          key="order-delivery-address-tab"
          tabPanelId="order-delivery-address"
        >
          <CustomerDetails contactData={order?.deliveryContact} />
        </TabPanel>,
        <TabPanel key="order-services-tab" tabPanelId="order-services">
          <ServiceList order={order} />
        </TabPanel>,
        <TabPanel
          key="order-associated-planner-drawing-tab"
          tabPanelId="order-associated-planner-drawing"
        >
          <AssociatedDrawings order={order} />
        </TabPanel>,
        <TabPanel key="order-attachments" tabPanelId="order-attachments">
          <OrderAttachments order={order} />
        </TabPanel>,
        <TabPanel
          key="order-custom-messages"
          tabPanelId="order-custom-messages"
        >
          <CustomMessagesList order={order} />
        </TabPanel>,
      ]}
    />
  );
};
